<template>
    <div v-if="error" class="text-center">
        <p class="text-red">{{error}}</p>
    </div>
    <div v-else-if="loading && !initialLoaded">
        <Preloader class="supercenter"/>
        <div class="supercenter mt-70px text-center">
            {{loading}} <span v-if="loadingProgress > 0">({{ loadingProgress }}%)</span>
            <div class="mt-8px text-center">{{ loadingWaiting }}</div>
        </div>
    </div>
    <div v-else>
        <PortfolioTable
            class="w-full"
            :groupedCoinsByGeckoId="groupedCoinsByGeckoId"
        />
    </div>
</template>

<script>
import { getCoinInfo, listCoins } from '@/api/coingecko'
import { getSheetsData } from '@/api/gSheets'
import { getGroupedCoins, getHoldingAssetsGeckoIds } from '@/services/transformers/groupedCoins'
import PortfolioTable from '@/components/PortfolioTable'
const sleep = ms => new Promise(r => setTimeout(r, ms));

export default {
    components: { PortfolioTable },
    props:{
        spreadsheets: Array, // [{key, tab}]
    },
    data(){
        return{
            portfolioData: [],
            currentCoingeckoFeedMap: {},
            initialLoaded: false,
            loading: 'Loading portfolio...',
            loadingProgress: 0,
            loadingWaiting: '',
            error: '',
        }
    },
    async created(){
        await this.getPortfolioData()
        await this.loadcurrentCoingeckoFeedMap()
        this.loading = false
        this.initialLoaded = true
        this.$emit('loaded')
    },
    computed:{
        groupedCoinsByGeckoId(){
            return getGroupedCoins(this.portfolioData, this.currentCoingeckoFeedMap)
        },
    },
    methods:{
        async getPortfolioData(){
            try {
                this.loading = 'Fetching portfolio data...'
                for(const {key, tab} of this.spreadsheets){
                    const sheetData = await getSheetsData(key, {date: 'Date', time: 'Time', transaction_type: 'Transaction type', asset: 'Asset', quantity: 'Quantity', total_price: 'Total', currency: 'Currency', gecko_id: 'Coingecko id'}, tab)
                    this.portfolioData = [...this.portfolioData, ...sheetData]
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error)
                this.$emit('invalidSpreadsheet')
            }
        },
        // loadcurrentCoingeckoFeedMap is also being called from parent component
        async loadcurrentCoingeckoFeedMap(){
            // get top 50 coins in one request at first, to avoid having to make lots of requests e.g. in case the coins are all in top 50
            let top50CoinsByGeckoId = {}
            try{
                this.loading = 'Fetching top 50 coins...'
                const top50Coins = await listCoins()
                top50CoinsByGeckoId = top50Coins.reduce((prevValue, curValue)=>{
                    prevValue[curValue.id] = curValue
                    return prevValue
                }, {})
            }catch(e){
                // sometimes returns a 403
                // let's just move on and use the getCoinInfo fallback for all coins
                console.error(e)
            }

            // get needed gecko ids
            const geckoIdsToFetch = getHoldingAssetsGeckoIds(this.portfolioData)

            // get potential needed gecko assets outside the top 50 as well
            // CoinGecko's Public API has a rate limit of 5 to 15 calls per minute
            const geckoAssets = [];
            for(const i in geckoIdsToFetch){
                const gecko_id = geckoIdsToFetch[i]

                this.loadingProgress = Math.round((i / geckoIdsToFetch.length) * 10000) / 100

                // if we already have the item from within the top 50, just return it
                if(top50CoinsByGeckoId[gecko_id]){
                    geckoAssets.push(top50CoinsByGeckoId[gecko_id])
                }else{
                    try{
                        this.loading = 'Fetching CoinGecko data for ' + gecko_id + '...'
                        const coininfo = await this.getCoinInfoRetry(gecko_id)
                        if(!coininfo){
                            continue
                        }
                        geckoAssets.push(coininfo)
                    }catch(e){
                        console.error(e)
                        this.error = "Something went wrong while fetching data from CoinGecko for " + gecko_id + ". Please try again later.";
                        return;
                    }
                }
            }

            // add to state
            for(const feedItem of geckoAssets){
                this.$set(this.currentCoingeckoFeedMap, feedItem.id, feedItem)
            }

            this.loadingProgress = 0;
        },
        async getCoinInfoRetry(gecko_id){
            try {
                return await getCoinInfo(gecko_id)
            }catch(e){
                console.log('err')
                console.log(e)

                const status = e.response.status;

                if(status === 429){
                    // too many requests
                    const retryAfter = e.response.headers['retry-after']
                    if(retryAfter){
                        console.log("Too many requests. Retrying after " + retryAfter + " seconds.")
                        await this.waitingForSeconds(retryAfter, 'Too many requests while fetching data for '+gecko_id+'. ')
                        return await this.getCoinInfoRetry(gecko_id)
                    }else{
                        console.log("Too many requests. No retry header found. Retrying after 60 seconds.")
                        await this.waitingForSeconds(60, 'Too many requests while fetching data for '+gecko_id+'. ')
                        return await this.getCoinInfoRetry(gecko_id)
                    }
                }

                if(status === 404){
                    // coin not found
                    console.log("Coin not found for "+gecko_id+". Skipping.")
                    return null
                }

                throw e
            }
        },
        async waitingForSeconds(seconds, prefix = ''){
            for(let i = seconds; i > 0; i--){
                this.loadingWaiting = prefix + 'Waiting for ' + i + ' seconds...'
                await sleep(1000)
            }
            this.loadingWaiting = ''
        },
    },
}
</script>
